// EXTERNAL DEPENDENCIES
import { useDispatch } from 'react-redux'

// HELPERS
import { trackImpression } from '@/utils'
import { useStaleRefetching } from '@/hooks'

// API
import { useEditProfile } from '@/api/profile'
import { formatProfileRequestBody } from '@/api/formatters'

// REDUX
import {
  setSaveStatus,
  updateEditPageErrorMessage,
  updateIsPreviewing,
} from '@/redux/features'
import { updateGlobalToast } from '@/redux/features/global-slice'

// TYPES
import { ProfileData, UnformattedFptiOptions } from '@/types'

/**
 * Hook to send a save edits request and display success/fail messages
 *
 * @returns a function to save given edits
 */
export function useSaveChanges({
  successMessage,
  errorMessage,
  onSuccess = () => {},
  onError = () => {},
}: {
  successMessage: string
  errorMessage: string
  onSuccess?: () => void
  onError?: () => void
}) {
  const dispatch = useDispatch()

  // NETWORK REQUESTS
  const refreshStaleData = useStaleRefetching()
  const { mutateAsync: editProfile } = useEditProfile()

  const saveEdits = async (
    data: ProfileData,
    fpti?: { success?: UnformattedFptiOptions; error?: UnformattedFptiOptions },
  ) => {
    try {
      dispatch(setSaveStatus('saving'))

      const request = formatProfileRequestBody(data, true)

      await editProfile({ request })

      fpti?.success && trackImpression(fpti.success)

      dispatch(updateEditPageErrorMessage(''))

      dispatch(
        updateGlobalToast({
          message: successMessage,
        }),
      )

      dispatch(setSaveStatus('success'))

      refreshStaleData()

      onSuccess()

      dispatch(setSaveStatus('idle'))
    } catch (_e) {
      dispatch(updateIsPreviewing(false))
      dispatch(setSaveStatus('error'))
      dispatch(updateEditPageErrorMessage(errorMessage))

      fpti?.error && trackImpression(fpti.error)

      onError()
    }
  }

  return saveEdits
}
